import React, { useState, useRef, useEffect } from 'react';

const MultiSelectDropdown = ({
  options,
  label,
  searchable = false,
  placeholder = 'Select options...',
  selectedValues = [],
  onSelect,
  onRemove,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredOptions, setFilteredOptions] = useState(options);
  const [selectedOptions, setSelectedOptions] = useState(selectedValues);
  const dropdownRef = useRef(null);

  useEffect(() => {
    setSelectedOptions(selectedValues); // Sync selected options with parent state
  }, [selectedValues]);

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
    setSearchTerm(''); // Reset search term when toggling dropdown
    setFilteredOptions(options); // Reset filtered options when dropdown is toggled
  };

  const handleSearch = (e) => {
    const term = e.target.value.toLowerCase();
    setSearchTerm(e.target.value);
    setFilteredOptions(
      options.filter((option) =>
        option.name.toLowerCase().includes(term)
      )
    );
  };

  const handleOptionClick = (option) => {
    let updatedSelectedOptions;
    const isSelected = selectedOptions.some((item) => item.id === option.id);

    if (isSelected) {
      updatedSelectedOptions = selectedOptions.filter((item) => item.id !== option.id);
      onRemove && onRemove(option, updatedSelectedOptions); // Pass option and updated list
    } else {
      updatedSelectedOptions = [...selectedOptions, option];
      onSelect && onSelect(option, updatedSelectedOptions); // Pass option and updated list
    }

    setSelectedOptions(updatedSelectedOptions); // Update local state
  };

  const handleClearAll = () => {
    const previouslySelected = [...selectedOptions];
    setSelectedOptions([]);
    onRemove && onRemove(null, []); // Notify parent about clearing all selections
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className="multi-select-dropdown" ref={dropdownRef}>
      <div className="dropdown-container" onClick={toggleDropdown}>
        <div className="selected-options">
          {selectedOptions.length > 0 ? (
            selectedOptions.map((option) => (
              <span key={option.id} className="tag">
                {option.name}
                <span
                  className="remove-tag"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOptionClick(option);
                  }}
                >
                  &times;
                </span>
              </span>
            ))
          ) : (
            <span style={{ pointerEvents: 'none', color: '#aaa' }}>
              {placeholder}
            </span>
          )}
        </div>
        <div className={`arrow ${isOpen ? 'open' : ''}`}>&#x25BC;</div>
      </div>

      {isOpen && (
        <div className="options-list">
          {searchable && (
            <input
              type="text"
              className="search-input"
              placeholder="Search..."
              value={searchTerm}
              onChange={handleSearch}
            />
          )}
          {filteredOptions.length > 0 ? (
            filteredOptions.map((option) => (
              <label key={option.id} className="option">
                <input
                  type="checkbox"
                  checked={selectedOptions.some((item) => item.id === option.id)} // Compare by id
                  onChange={() => handleOptionClick(option)}
                />
                {option.name}
              </label>
            ))
          ) : (
            <div className="no-options">No options found</div>
          )}
          {selectedOptions.length > 0 && !searchTerm && (
            <div className="clear-all" onClick={handleClearAll}>
              Clear All
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MultiSelectDropdown;
